import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  TrendingUp,
  Users,
  Calendar
} from 'lucide-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Link } from 'react-router-dom';
import { parseToISO, isDateOverdue, getDaysFromDue, getTodayISO } from '../utils/dateUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format user display with organization tag
const formatOwnerDisplay = (ownerData) => {
  if (!ownerData || ownerData.owner === 'Unassigned') return 'Unassigned';
  const name = ownerData.owner || 'Unknown';
  
  // Use display_group if available
  if (ownerData.display_group) {
    return `${name} (${ownerData.display_group})`;
  }
  
  if (ownerData.role === 'external_org' && ownerData.organization) {
    return `${name} (${ownerData.organization})`;
  } else if (ownerData.organization && ownerData.organization !== 'HLRA') {
    return `${name} (${ownerData.organization})`;
  } else {
    return name;
  }
};

// Get organization badge color
const getOrgBadgeColor = (ownerData) => {
  if (!ownerData || ownerData.owner === 'Unassigned') return 'text-gray-500';
  if (ownerData.display_group_color) {
    return ''; // Will use inline style
  }
  if (ownerData.role === 'external_org' || (ownerData.organization && ownerData.organization !== 'HLRA')) {
    return 'text-purple-600';
  }
  return 'text-blue-600';
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [overdueActions, setOverdueActions] = useState([]);
  const [dueTodayActions, setDueTodayActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, actionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/actions`),
        axios.get(`${API}/users/with-groups`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      
      // Filter and sort overdue actions (most overdue first) using robust date parsing
      // Exclude deferred actions from overdue list
      const overdue = actionsRes.data
        .filter(action => 
          action.due_date && 
          isDateOverdue(action.due_date) && 
          !['Completed', 'Duplicate', 'Not Required', 'Deferred'].includes(action.status)
        )
        .sort((a, b) => {
          const dateA = parseToISO(a.due_date);
          const dateB = parseToISO(b.due_date);
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.localeCompare(dateB);
        })
        .slice(0, 10); // Show up to 10 overdue actions
      setOverdueActions(overdue);
      
      // Filter actions due today using robust date parsing
      const today = getTodayISO();
      const dueToday = actionsRes.data
        .filter(action => 
          action.due_date && 
          parseToISO(action.due_date) === today && 
          !['Completed', 'Duplicate', 'Not Required'].includes(action.status)
        )
        .slice(0, 10); // Show up to 10 due today actions
      setDueTodayActions(dueToday);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate days overdue using robust date parsing
  const getDaysOverdue = (dueDate) => {
    return getDaysFromDue(dueDate);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  // Status Distribution - shows only OPEN/active statuses (not Completed/Duplicate/Not Required)
  const statusData = {
    labels: ['Not Started', 'In Progress', 'Deferred'],
    datasets: [{
      data: [
        stats?.not_started_actions || 0,
        stats?.in_progress_actions || 0,
        stats?.deferred_actions || 0
      ],
      backgroundColor: ['#94a3b8', '#3b82f6', '#f59e0b'],
      borderWidth: 0
    }]
  };

  const priorityData = {
    labels: ['Low', 'Medium', 'High', 'Urgent'],
    datasets: [{
      label: 'Actions by Priority',
      data: [
        stats?.actions_by_priority?.Low || 0,
        stats?.actions_by_priority?.Medium || 0,
        stats?.actions_by_priority?.High || 0,
        stats?.actions_by_priority?.Urgent || 0
      ],
      backgroundColor: ['#0ea5e9', '#f59e0b', '#fb923c', '#ef4444']
    }]
  };

  const assigneeData = {
    labels: (stats?.actions_by_owner || []).map(a => {
      if (a.owner === 'Unassigned') return 'Unassigned';
      const org = (a.role === 'external_org' || (a.organization && a.organization !== 'HLRA')) 
        ? a.organization 
        : 'HLRA';
      return `${a.owner} (${org || 'HLRA'})`;
    }),
    datasets: [{
      label: 'Actions by Assignee',
      data: (stats?.actions_by_owner || []).map(a => a.count),
      backgroundColor: [
        '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
        '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
      ]
    }]
  };

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Overview of action items and meeting progress</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover border-l-4 border-l-blue-500" data-testid="total-actions-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Actions</CardTitle>
            <Calendar className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.total_actions || 0}</div>
            <div className="flex gap-3 mt-1">
              <span className="text-xs text-blue-600">{stats?.active_actions || 0} open</span>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-green-600">{(stats?.completed_actions || 0) + (stats?.duplicate_actions || 0) + (stats?.not_required_actions || 0)} closed</span>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-orange-500">{stats?.archived_actions || 0} archived</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-l-4 border-l-green-500" data-testid="completed-actions-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Completed</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.completed_actions || 0}</div>
            <p className="text-xs text-green-600 mt-1">{stats?.completion_rate || 0}% completion rate</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-l-4 border-l-yellow-500" data-testid="in-progress-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">In Progress</CardTitle>
            <Clock className="w-4 h-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.in_progress_actions || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Active items</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-l-4 border-l-red-500" data-testid="overdue-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Overdue</CardTitle>
            <AlertCircle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats?.overdue_actions || 0}</div>
            <p className="text-xs text-red-600 mt-1">Requires attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Action Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <Doughnut data={statusData} options={{ maintainAspectRatio: false }} />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Actions by Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar 
                data={priorityData} 
                options={{ 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  }
                }} 
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="assignee-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Actions by Assignee
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {(stats?.actions_by_owner || []).length === 0 ? (
                <p className="text-gray-500 text-center py-8">No assignee data</p>
              ) : (
                <Bar 
                  data={assigneeData} 
                  options={{ 
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      x: { 
                        beginAtZero: true,
                        ticks: { stepSize: 1 }
                      }
                    }
                  }} 
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Actions */}
      <Card className="card-hover border-t-4 border-t-red-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            Overdue Actions
            {overdueActions.length > 0 && (
              <span className="ml-2 text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                {overdueActions.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {overdueActions.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">No overdue actions!</p>
              <p className="text-gray-500 text-sm">All actions are on track</p>
            </div>
          ) : (
            <div className="space-y-3">
              {overdueActions.map((action) => {
                const daysOverdue = getDaysOverdue(action.due_date);
                const owner = users.find(u => u.id === action.owner_id);
                const ownerDisplay = owner ? (
                  (owner.role === 'external_org' || (owner.organization && owner.organization !== 'HLRA'))
                    ? `${owner.name} (${owner.organization})`
                    : `${owner.name} (HLRA)`
                ) : 'Unassigned';
                const ownerColor = owner ? (
                  (owner.role === 'external_org' || (owner.organization && owner.organization !== 'HLRA'))
                    ? 'text-purple-700'
                    : 'text-blue-700'
                ) : 'text-gray-600';
                return (
                  <Link
                    key={action.id}
                    to={`/actions/${action.id}`}
                    className="flex items-center justify-between p-4 border-l-4 border-l-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition"
                    data-testid={`overdue-action-${action.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-red-700">{action.reference}</span>
                        <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-medium">
                          {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue
                        </span>
                      </div>
                      <h4 className="font-bold text-red-800 mt-1">{action.title}</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Due: {new Date(action.due_date).toLocaleDateString('en-GB')}
                        {' • '}Assigned to: <span className={ownerColor}>{ownerDisplay}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`status-badge status-${action.status.toLowerCase().replace(' ', '-')}`}>
                        {action.status}
                      </span>
                      <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                        {action.priority}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Due Today Actions */}
      <Card className="card-hover border-t-4 border-t-orange-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Clock className="w-5 h-5" />
            Due Today
            {dueTodayActions.length > 0 && (
              <span className="ml-2 text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                {dueTodayActions.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dueTodayActions.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">No actions due today!</p>
              <p className="text-gray-500 text-sm">You're all caught up for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dueTodayActions.map((action) => {
                const owner = users.find(u => u.id === action.owner_id);
                const ownerDisplay = owner ? (
                  (owner.role === 'external_org' || (owner.organization && owner.organization !== 'HLRA'))
                    ? `${owner.name} (${owner.organization})`
                    : `${owner.name} (HLRA)`
                ) : 'Unassigned';
                const ownerColor = owner ? (
                  (owner.role === 'external_org' || (owner.organization && owner.organization !== 'HLRA'))
                    ? 'text-purple-700'
                    : 'text-blue-700'
                ) : 'text-gray-600';
                return (
                <Link
                  key={action.id}
                  to={`/actions/${action.id}`}
                  className="flex items-center justify-between p-4 border-l-4 border-l-orange-500 bg-orange-50 rounded-lg hover:bg-orange-100 transition"
                  data-testid={`due-today-action-${action.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-orange-700">{action.reference}</span>
                      <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-medium animate-pulse">
                        DUE TODAY
                      </span>
                    </div>
                    <h4 className="font-bold text-orange-800 mt-1">{action.title}</h4>
                    <p className="text-sm text-orange-600 mt-1">
                      Assigned to: <span className={ownerColor}>{ownerDisplay}</span>
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`status-badge status-${action.status.toLowerCase().replace(' ', '-')}`}>
                      {action.status}
                    </span>
                    <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                      {action.priority}
                    </span>
                  </div>
                </Link>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}